
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const MeasurementBillPDF = {
  generate: async (rowData, logoUrl, companyInfo) => {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 15;
      let yPos = margin;
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      // Utility to add footer
      function addFooter(doc, companyInfo, pageWidth, pageHeight, margin) {
        const footerY = pageHeight - 10;
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
        doc.text(`Generated on ${new Date().toLocaleString()}`, margin, footerY);
        doc.setFont('helvetica', 'bold');
        doc.text(companyInfo.name || '', pageWidth / 2, footerY, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.text(companyInfo.footerText || 'Thank you for your business!', pageWidth - margin, footerY, { align: 'right' });
      }

      // HEADER
      if (logoUrl) {
        await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            doc.addImage(img, 'PNG', margin, yPos, 40, 20);
            resolve();
          };
          img.onerror = resolve;
          img.src = logoUrl;
        });
      }


      doc.setFontSize(10);
      doc.setTextColor(41, 128, 185); // heading in blue
      doc.setFont('helvetica', 'bold');
      doc.text('Customer Name:', margin, yPos + 5);

      doc.setTextColor(0, 0, 0); // value in black
      doc.setFont('helvetica', 'normal');
      doc.text(companyInfo.name || 'N/A', margin + 32, yPos + 5); // reduced from +40 to +32

      // Project Name (Line 2)
      doc.setTextColor(41, 128, 185);
      doc.setFont('helvetica', 'bold');
      doc.text('Project Name:', margin, yPos + 10);

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(companyInfo.project || 'N/A', margin + 32, yPos + 10);

      yPos += 20;

      // TITLE
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text('MEASUREMENT BILL', pageWidth / 2, yPos, { align: 'center' });

      doc.setLineWidth(0.5);
      doc.line(margin, yPos + 5, pageWidth - margin, yPos + 5);
      yPos += 15;
      // PO Number and Date (same row)
      doc.setFontSize(11).setFont('helvetica', 'bold');
      doc.text('MB Number:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(rowData.mbNumber || '', margin + 25, yPos);

      doc.setFont('helvetica', 'bold');
      doc.text('Date:', margin + 60, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(
        rowData.mbDate
          ? new Date(rowData.mbDate).toLocaleDateString('en-GB')
          : '',
        margin + 73,
        yPos
      );


      yPos += 7;


      const maxLineWidth = 79;
      const lineHeight = 5;

      const billingLines = [
        rowData.customer?.name || '',
        ...(doc.splitTextToSize(rowData.billingAddress?.address || '', maxLineWidth)),
        ...(doc.splitTextToSize(
          `${rowData.billingAddress?.city || ''}, ${rowData.billingAddress?.state || ''} - ${rowData.billingAddress?.pincode || ''}`,
          maxLineWidth
        ))
      ];

      const shippingLines = [
        rowData.customer?.name || '',
        ...(doc.splitTextToSize(rowData.dispatchAddress?.address || '', maxLineWidth)),
        ...(doc.splitTextToSize(
          `${rowData.dispatchAddress?.city || ''}, ${rowData.dispatchAddress?.state || ''} - ${rowData.dispatchAddress?.pincode || ''}`,
          maxLineWidth
        ))
      ];

      // Calculate box height: line count + GST line + padding
      const billingBoxHeight = ''
      const shippingBoxHeight = ''
      // const billingBoxHeight = (billingLines.length + 1) * lineHeight + 3;
      // const shippingBoxHeight = (shippingLines.length + 1) * lineHeight + 3;

      doc.setFontSize(10);

      // Billing Box
      // doc.setFont('helvetica', 'bold');
      // doc.text('BILLING TO:', margin, yPos + 5);
      // doc.setFont('helvetica', 'normal');
      // doc.setFillColor(245, 245, 245);
      // doc.rect(margin, yPos + 10, 85, billingBoxHeight, 'F');
      // billingLines.forEach((line, i) => {
      //   doc.text(line, margin + 3, yPos + 15 + i * lineHeight);
      // });
      if (rowData.customer?.gst) {
        doc.setFont('helvetica', 'bold');
        doc.text(`GST No: ${rowData.customer.gst}`, margin + 3, yPos + 15 + billingLines.length * lineHeight);
      }

      // Shipping Box
      // doc.setFont('helvetica', 'bold');
      // doc.text('SHIPPED TO:', margin + 95, yPos + 5);
      // doc.setFont('helvetica', 'normal');
      // doc.setFillColor(245, 245, 245);
      // doc.rect(margin + 95, yPos + 10, 85, shippingBoxHeight, 'F');
      // shippingLines.forEach((line, i) => {
      //   doc.text(line, margin + 98, yPos + 15 + i * lineHeight);
      // });
      if (rowData.customer?.gst) {
        doc.setFont('helvetica', 'bold');
        doc.text(`GST No: ${rowData.customer.gst}`, margin + 98, yPos + 15 + shippingLines.length * lineHeight);
      }


      const boxBottom = yPos + 10 + Math.max(billingBoxHeight, shippingBoxHeight);
      yPos = boxBottom + 10;

      // ITEMS TABLE
      const columns = [
        { header: '#', dataKey: 'serialNumber' },
        { header: 'Description', dataKey: 'description' },
        { header: 'HSN', dataKey: 'hsnCode' },
        { header: 'Qty', dataKey: 'quantity' },
        { header: 'Unit', dataKey: 'uom' },
        { header: 'Rate', dataKey: 'rate' },
        { header: 'Taxable Value', dataKey: 'taxableValue' },
        { header: 'GST Amount', dataKey: 'gstValue' },
        { header: 'Total Amount', dataKey: 'totalValue' }
      ];

      const tableData = rowData.items.map((item, index) => ({
        serialNumber: index + 1,
        description: `${item.item?.name || ''}\n${item.item?.description || ''}`,
        hsnCode: item.hsnCode || '',
        quantity: item?.workOrderItem?.quantity || 0,
        uom: item.uom || '',
        rate: Number(item?.workOrderItem?.rate || 0).toFixed(2),
        taxableValue: Number(item?.workOrderItem?.taxableValue || 0).toFixed(2),
        gstValue: Number(item?.workOrderItem?.gstValue || 0).toFixed(2),
        totalValue: Number(item?.workOrderItem?.totalValue || 0).toFixed(2),
        labourName: item?.workOrderItem?.labour?.name || ''
      }));

      const subtotal = rowData.items.reduce(
        (sum, item) => sum + Number(item?.workOrderItem?.taxableValue || 0),
        0
      );

      const taxTotal = rowData.items.reduce(
        (sum, item) => sum + Number(item?.workOrderItem?.gstValue || 0),
        0
      );

      const grandTotal = subtotal + taxTotal;

      doc.autoTable({
        startY: yPos,
        head: [columns.map(c => c.header)],
        body: tableData.map(row => columns.map(col => row[col.dataKey])),
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 9,
          halign: 'center',
          valign: 'middle'
        },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { cellWidth: 35 },
          2: { cellWidth: 20 },
          3: { cellWidth: 12 },
          4: { cellWidth: 15 },
          5: { cellWidth: 20, halign: 'right' },
          6: { cellWidth: 25, halign: 'right' },
          7: { cellWidth: 25, halign: 'right' },
          8: { cellWidth: 25, halign: 'right' }
        },
        didDrawPage: function () {
          addFooter(doc, companyInfo, pageWidth, pageHeight, margin);
        }
      });

      yPos = doc.lastAutoTable.finalY + 10;
      const spaceNeededForTotals = 30; // estimate space needed for totals
      const footerThreshold = pageHeight - 20;

      if (yPos + spaceNeededForTotals > footerThreshold) {
        doc.addPage();
        addFooter(doc, companyInfo, pageWidth, pageHeight, margin);
        yPos = margin;
      }

      // TOTALS
      doc.setFontSize(10).setFont('helvetica', 'bold');
      doc.text('Subtotal:', pageWidth - margin - 60, yPos);
      doc.text(`${subtotal.toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });

      yPos += 7;
      doc.text('Tax (GST):', pageWidth - margin - 60, yPos);
      doc.text(`${taxTotal.toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });

      yPos += 7;
      doc.setFontSize(11);
      doc.setTextColor(220, 53, 69);
      doc.text('Grand Total:', pageWidth - margin - 60, yPos);
      doc.text(`${grandTotal.toFixed(2)}`, pageWidth - margin, yPos, { align: 'right' });
      doc.setTextColor(51, 51, 51);
      yPos += 15;



      // Save the final PDF
      doc.save(`MB_${rowData.mbNumber}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      const doc = new jsPDF();
      doc.text('Error generating PDF', 10, 10);
      doc.text(err.message, 10, 20);
      doc.save('error.pdf');
    }
  }
};

export default MeasurementBillPDF;
